/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useAsyncDebounce } from "react-table";
import { Card, ControlledTable, Input, SvgIcon } from "core/components";
import { pending_site_deliveries_path, print_admin_purchase_order_path, site_purchase_order_path } from "routes";
import { navigateTo } from "core/navigation";
import { api } from "core/api";
import moment from "moment";
import ControlledTableContext from "core/components/ControlledTableContext";

type PendingDeliveriesProps = {
  has_purchase_orders: boolean;
  has_warehouses_access: boolean;
};

const PendingDeliveries = ({ has_purchase_orders, has_warehouses_access }: PendingDeliveriesProps): JSX.Element => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [purchaseOrders, setPurchaseOrders] = React.useState([]);
  const [pagination, setPagination] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const table = React.useRef(null);
  const abortController = React.useRef<AbortController>();
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: "code",
        Header: "Purchase Order",
        accessor: "code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        id: "supplier",
        Header: "Supplier",
        accessor: "supplier.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: "warehouse",
        Header: "Warehouse",
        accessor: "warehouse.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Expected Delivery Date",
        accessor: "expected_delivery_date",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{moment(props.value).format("MM/DD/YYYY")}</p>
            </div>
          );
        }
      },
      {
        Header: "Ordered By",
        accessor: "created_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Total Items",
        accessor: "total_undelivered_items",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "",
        Cell: (props) => {
          return (
            <div className="d-flex justify-content-center">
              <a
                className="button button--action button--action-approve"
                title="View Purchase Order"
                data-bs-toggle="tooltip"
                onClick={() => {
                  navigateTo(site_purchase_order_path(props.row.original.id));
                }}
              >
                <SvgIcon name="view" className="h5" />
              </a>
              <a
                className="button button--action button--action-primary"
                title="Print Purchase Order"
                data-bs-toggle="tooltip"
                onClick={() => {
                  window.open(print_admin_purchase_order_path(props.row.original.id));
                }}
              >
                <SvgIcon name="printer" className="h5" />
              </a>
            </div>
          );
        }
      }
    ],
    []
  );

  const getPurchaseOrders = React.useCallback(async ({ page, per, filters }) => {
    try {
      setIsLoading(true);
      // If there is a pending fetch request with associated AbortController, abort
      if (abortController.current) abortController.current.abort();
      // Assign a new AbortController for the latest fetch to our useRef variable
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const response = await api.get(pending_site_deliveries_path({ page, per, filters: JSON.stringify(filters) }), { signal });
      setPurchaseOrders(response.data.data);
      setPagination(response.data.pagination);
      setCurrentPage(response.data.pagination?.current || currentPage);
      return { pagination: response.data.pagination, data: response.data.data };
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onChange = useAsyncDebounce((column, value) => {
    table.current.setFilter(column, value);
  }, 400);

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row>
          <Col>
            <SvgIcon name="truck" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No purchase orders for delivery.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Purchase Order Number" onChange={(e) => onChange("code", e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Supplier" onChange={(e) => onChange("supplier", e.target.value)} />
        </Col>
        {has_warehouses_access && (
          <Col>
            <Input placeholder="Search for Warehouse" onChange={(e) => onChange("warehouse", e.target.value)} />
          </Col>
        )}
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <ControlledTableContext.Provider value={{ currentPage, setCurrentPage }}>
            <ControlledTable
              columns={columns}
              data={purchaseOrders}
              ref={table}
              isLoading={isLoading}
              fetchData={getPurchaseOrders}
              totalCount={pagination?.total_count}
              totalPageCount={pagination?.total_pages || 0}
              previous={pagination?.previous}
              next={pagination?.next}
            />
          </ControlledTableContext.Provider>
        </Col>
      </Row>
    );
  };

  const content = () => {
    return has_purchase_orders ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  return (
    <React.StrictMode>
      <Container fluid={true}>
        <Row>
          <Col>
            <Card>
              <Card.Header title="Deliveries" />
              <Card.Body>{content()}</Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </React.StrictMode>
  );
};

export default PendingDeliveries;
